'use client'

import { useState } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import {
  GenerateLabelModal,
  clickEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { DigitalLabelOffer, useFetchDigitalLabelOffer } from '@marketplace-web/ads/ads-feature'

import DigitalLabelModal from '../DigitalLabelModal'
import DropOffPointModal from '../DropOffPointModal'

type Props = ConversationActionProps

const OpenQrCode = ({ primary, title, inverse, theme, size }: Props) => {
  const { track } = useTracking()
  const { conversation } = useConversationContext()

  const [visibleModal, setVisibleModal] = useState<GenerateLabelModal>(GenerateLabelModal.None)

  const digitalLabelOfferAbTest = useAbTest('van_digital_shipping_label_ad')
  const trackExpose = useTrackAbTestCallback()

  const variantAOffer = useFetchDigitalLabelOffer({
    abTestVariant: 'a',
    shouldFetchOffer: visibleModal === GenerateLabelModal.OpenQrCodeModal,
  })

  const variantBOffer = useFetchDigitalLabelOffer({
    abTestVariant: 'b',
    shouldFetchOffer: visibleModal === GenerateLabelModal.OpenDigitalLabelOfferModal,
  })

  const transactionId = conversation?.transaction?.id

  function trackButtonClick() {
    const targetDetails = { transaction_id: transactionId }

    track(
      clickEvent({
        screen: 'message_reply',
        target: 'open_digital_label',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function handleButtonClick() {
    trackExpose(digitalLabelOfferAbTest)
    trackButtonClick()
    setVisibleModal(GenerateLabelModal.OpenQrCodeModal)
  }

  function handleModalClose() {
    setVisibleModal(GenerateLabelModal.None)
  }

  function handleDigitalLabelModalOpen() {
    setVisibleModal(GenerateLabelModal.OpenQrCodeModal)
  }

  function handleDigitalLabelOfferOpen() {
    variantAOffer.trackBannerOfferClick()
    setVisibleModal(GenerateLabelModal.OpenDigitalLabelOfferModal)
  }

  function handleDropOffPointModalOpen() {
    setVisibleModal(GenerateLabelModal.DropOffPointMapModal)
  }

  function handleDigitalLabelModalClose() {
    if (digitalLabelOfferAbTest?.variant === 'b' && !variantBOffer.shouldSkipDueToSession) {
      setVisibleModal(GenerateLabelModal.OpenDigitalLabelOfferModal)

      return
    }

    setVisibleModal(GenerateLabelModal.None)
  }

  return (
    <>
      <ConversationActionButton
        primary={primary}
        text={title}
        size={size}
        inverse={inverse}
        theme={theme}
        onClick={handleButtonClick}
        testId="open-digital-label-button"
      />
      <DigitalLabelModal
        isOpen={visibleModal === GenerateLabelModal.OpenQrCodeModal}
        onClose={handleDigitalLabelModalClose}
        onDropOffPointModalOpen={handleDropOffPointModalOpen}
        offerBanner={
          digitalLabelOfferAbTest?.variant === 'a'
            ? {
                isVisible: variantAOffer.hasOffers,
                isMultipleOffers: variantAOffer.placements.length > 1,
                isLoading: variantAOffer.isLoading,
                onClick: handleDigitalLabelOfferOpen,
              }
            : null
        }
      />
      <DigitalLabelOffer
        isOpen={visibleModal === GenerateLabelModal.OpenDigitalLabelOfferModal}
        offerPlacements={
          digitalLabelOfferAbTest?.variant === 'a'
            ? variantAOffer.placements
            : variantBOffer.placements
        }
        isLoading={
          digitalLabelOfferAbTest?.variant === 'a'
            ? variantAOffer.isLoading
            : variantBOffer.isLoading
        }
        onOfferShown={
          digitalLabelOfferAbTest?.variant === 'b' ? variantBOffer.markOfferAsShown : null
        }
        shouldShowBackNav={digitalLabelOfferAbTest?.variant === 'a'}
        onClose={handleModalClose}
        onNavigateBack={handleDigitalLabelModalOpen}
      />
      <DropOffPointModal
        isOpen={visibleModal === GenerateLabelModal.DropOffPointMapModal}
        onClose={handleDigitalLabelModalOpen}
      />
    </>
  )
}

export default OpenQrCode
